import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'sushiweb/Icons';
import { useMutationCall } from '../../api/react-client';
import { useQueries } from 'react-query';
import { useClient } from '../../api/api-client';
import { componentMap } from '../serverDrivenUIComponents/compMapping';
import {
  helpDeskClearFiltersEvent,
  helpDeskFilterChangeEvent,
} from '../../analytics/events';
import { MetricsForJumboEvent } from '../../analytics/constants';

const filtersList = ['complaint_type', 'items_name', 'complaint_amount'];

const SearchWithFilters = ({
  orderId,
  searchData,
  setSearchResults,
  setClickedToHome,
  setNodesFetching = () => {},
}) => {
  const get_complaint_types_endpoint = `lifeline_apis/helpdesk/fetch_complaint_types?order_id=${orderId}`;
  const get_order_items_endpoint = `lifeline_apis/helpdesk/fetch_order_items?order_id=${orderId}`;
  const client = useClient();
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [filterValues, setFilterValues] = useState();
  const [filterValuesToShow, setFilterValuesToShow] = useState();
  const [clearInputs, setClearInputs] = useState(false);
  let timerForComplaintAmt = null;

  const [complaintTypesResults, orderItemsResults] = useQueries([
    {
      queryKey: ['fetch_complaint_types', orderId],
      queryFn: () => {
        return client(`${get_complaint_types_endpoint}`);
      },
      enabled: false,
    },
    {
      queryKey: ['fetch_order_items', `${orderId}_${orderId}`],
      queryFn: () => {
        return client(`${get_order_items_endpoint}`);
      },
      enabled: false,
    },
  ]);

  const {
    mutate: searchNodes,
    isLoading: searchingNodes,
    data: searchResults,
  } = useMutationCall({}, `helpdesk/fetch_help_nodes`, [], {
    doNotShowSuccessToast: true,
  });

  useEffect(() => {
    if (!filterValues) return;
    const payloadToFetchNodes = {
      ...filterValues,
      context: 'partially_enabled',
      order_id: orderId,
      magnify: 'enabled',
    };
    searchNodes(payloadToFetchNodes);
    setNodesFetching(true);
  }, [filterValues]);

  useEffect(() => {
    if (searchResults) setSearchResults(searchResults);
    if (!searchingNodes) setNodesFetching(false);
  }, [searchResults]);

  useEffect(() => {
    if (orderId) {
      complaintTypesResults.refetch();
      orderItemsResults.refetch();
    }
  }, [orderId]);

  useEffect(() => {
    const orderItemCategoryMap = {};
    const complaintSlugTypeMap = {};

    const orderItems = orderItemsResults?.data;
    for (let i = 0; i < orderItems?.length; i++) {
      const itemId = orderItems[i].id;
      orderItemCategoryMap[itemId] = {
        categories: orderItems[i].categories,
        name: orderItems[i].name,
      };
    }
    const complaintTypes = complaintTypesResults?.data;
    for (let i = 0; i < complaintTypes?.length; i++) {
      const complaintTypeSlug = complaintTypes[i].id;
      complaintSlugTypeMap[complaintTypeSlug] = complaintTypes[i].name;
    }
    setFilters({
      ...filters,
      complaint_type: {
        type: 'select',
        data: complaintTypesResults.data,
        placeholder: 'Select Issue Type',
        saveOption: (key, value) => {
          saveFilter(key, value, complaintSlugTypeMap);
        },
      },
      items_name: {
        type: 'select',
        data: orderItemsResults.data,
        placeholder: 'Select Item Name',
        saveOption: (key, value) => {
          handleOrderItems(key, value, orderItemCategoryMap);
          handleFilterChangeEvent(key, value);
        },
      },
      complaint_amount: {
        type: 'text',
        data: null,
        placeholder: 'Enter Complaint Amt',
        saveOption: saveFilter,
      },
    });
  }, [complaintTypesResults?.data, orderItemsResults?.data, filterValues]);

  const handleFilterChangeEvent = (key, value) => {
    helpDeskFilterChangeEvent(
      {
        [MetricsForJumboEvent.order_id]: orderId,
        [MetricsForJumboEvent.feature_meta]: {
          filter_key: key,
          filter_value: value,
        },
      },
      key,
    );
  };

  const saveFilter = (key, value, filterMap = {}) => {
    if (key === 'complaint_amount') {
      if (timerForComplaintAmt) clearTimeout(timerForComplaintAmt);
      timerForComplaintAmt = setTimeout(() => {
        setFilterValues({
          ...filterValues,
          [key]: value,
        });
        setFilterValuesToShow({
          ...filterValuesToShow,
          [key]: value,
        });
        handleFilterChangeEvent(key, value);
      }, 800);
    } else {
      setFilterValues({
        ...filterValues,
        [key]: value,
      });
      setFilterValuesToShow({
        ...filterValuesToShow,
        [key]: filterMap[value],
      });
      handleFilterChangeEvent(key, value);
    }
    setClearInputs(false);
  };

  const handleOrderItems = (key, value, orderItemCategoryMap) => {
    if (!orderItemCategoryMap || !orderItemCategoryMap[value]) return;
    setFilterValues({
      ...filterValues,
      item_categories: orderItemCategoryMap[value]['categories'],
    });
    setFilterValuesToShow({
      ...filterValuesToShow,
      [key]: orderItemCategoryMap[value]['name'],
    });
    setClearInputs(false);
  };

  const handleFilterState = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFilterOpen((prevState) => !prevState);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFilterValues(null);
    setClearInputs(true);
    helpDeskClearFiltersEvent({
      [MetricsForJumboEvent.order_id]: orderId,
      [MetricsForJumboEvent.feature_meta]: {
        ...filterValues,
      },
    });
  };

  return (
    <div className='tw-w-full tw-p-4 tw-gap-3 tw-flex tw-flex-col tw-justify-between tw-bg-indigo-100 tw-border-[0.1rem] tw-border-solid tw-border-indigo-200 tw-rounded-xl '>
      <div
        className='tw-w-full tw-flex  tw-justify-between'
        onClick={handleFilterState}
      >
        <div className='tw-text-300 tw-font-medium tw-text-green-700 '>
          Filters
        </div>
        <div>
          {filterValues ? (
            <span
              className='tw-text-200 tw-font-medium tw-mr-1  tw-text-green-700 tw-cursor-pointer'
              onClick={(e) => {
                clearFilters(e);
              }}
            >
              Clear
            </span>
          ) : null}

          {filterOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {filterOpen ? (
        <div className='tw-flex tw-flex-col tw-w-full tw-bg-white tw-rounded-xl'>
          {filtersList.map((filter, index) => {
            const fieldType = filters[filter]?.type;
            const CompName = componentMap[fieldType];
            return (
              <div
                className={`tw-w-full  tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-flex  tw-flex-col tw-justify-between ${
                  index < filters.length - 1
                    ? 'tw-border-[1px]  tw-border-solid tw-border-indigo-200px'
                    : ''
                } tw-text-100 tw-font-medium`}
                key={`${filter}_${index}`}
                css={`
                  & div,
                  input {
                    border: none !important;
                    font-size: 1.2rem !important;
                    font-weight: 500 !important;
                  }
                  & input:focus {
                    border: none !important;
                    outline: 0.15rem solid #cfcfcf !important;
                    border-top-left-radius: 0px;
                    border-top-right-radius: 0px;
                    -webkit-appearance: none !important;
                  }
                `}
              >
                <CompName
                  field={filters[filter]}
                  inputKey={filter}
                  selectOptions={filters[filter]?.data || []}
                  saveOptions={filters[filter].saveOption}
                  unSaveOptions={() => {}}
                  isMultiSelect={false}
                  disabled={false}
                  defaultValue={filterValuesToShow?.[filter]}
                  clearInputs={clearInputs}
                />
                {index < filtersList.length - 1 ? (
                  <hr width='2' size='1' color='#D9DDEE' className='tw-m-0' />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default SearchWithFilters;
